import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Icon } from '@/lib/icons';
import { cn } from '@/lib/cn';
import { modalBackdrop, modalContent, modalTransition } from '@/lib/motion';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  closeOnBackdrop?: boolean;
}

const SIZE_CLASSES: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  closeOnBackdrop = true,
}: ModalProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // ESC + Focus-Trap
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const node = containerRef.current;

    function focusableInside(): HTMLElement[] {
      if (!node) return [];
      return Array.from(
        node.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea, input:not([type="hidden"]):not([disabled]), select, [tabindex]:not([tabindex="-1"])',
        ),
      );
    }

    // initial focus
    const first = focusableInside()[0];
    first?.focus();

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        const items = focusableInside();
        if (items.length === 0) return;
        const idx = items.indexOf(document.activeElement as HTMLElement);
        if (e.shiftKey) {
          if (idx <= 0) {
            e.preventDefault();
            items[items.length - 1]?.focus();
          }
        } else if (idx === items.length - 1) {
          e.preventDefault();
          items[0]?.focus();
        }
      }
    }

    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      previouslyFocused?.focus?.();
    };
  }, [open, onClose]);

  // Ueber ein Portal direkt an <body>. `position: fixed` richtet sich sonst
  // nicht am Fenster aus, sondern am naechsten Vorfahren mit `transform`,
  // `filter` oder `backdrop-filter` — und die Kopfzeile hat `backdrop-blur`.
  // Ein Dialog, der von dort geoeffnet wird (z. B. die Rueckmeldung), wurde
  // deshalb in einem 56 px hohen Kasten zentriert und ragte oben aus dem Bild.
  const dialog = (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="fixed inset-0 z-50 overflow-y-auto overscroll-contain"
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={modalBackdrop}
          transition={modalTransition}
        >
          <button
            type="button"
            aria-label="Schließen"
            onClick={closeOnBackdrop ? onClose : undefined}
            className="fixed inset-0 cursor-default bg-navy-950/50 backdrop-blur-sm"
          />
          {/* Oben ausgerichtet, sobald es eng wird: ein zentrierter Dialog
              waechst nach beiden Seiten aus dem Bild, ein oben verankerter
              laesst sich herunterscrollen. Ab sm wieder mittig. */}
          <div className="relative flex min-h-full items-start justify-center p-4 sm:items-center">
            <motion.div
              ref={containerRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-title"
              aria-describedby={description ? 'modal-description' : undefined}
              variants={modalContent}
              transition={modalTransition}
              className={cn(
                // Hoehe begrenzen und den Inhalt scrollen lassen. Ohne das wuchs
                // ein langer Dialog aus dem Fenster heraus — weil die Huelle
                // zentriert, verschwand der obere Teil samt Ueberschrift und
                // Schliessen-Knopf nach oben aus dem Bild. `dvh` statt `vh`,
                // weil mobile Browser die Adressleiste ein- und ausblenden.
                'relative flex max-h-[calc(100vh-2rem)] w-full flex-col overflow-hidden rounded-lg border border-ink-200 bg-paper shadow-elev-4',
                'supports-[max-height:1dvh]:max-h-[calc(100dvh-2rem)]',
                SIZE_CLASSES[size],
              )}
            >
              {/* Kopf und Fuss bleiben stehen, nur der Inhalt scrollt — sonst
                verliert man beim Blaettern den Schliessen-Knopf. */}
              <div className="flex shrink-0 items-start justify-between gap-3 border-b border-ink-200 px-6 py-4">
                <div className="min-w-0">
                  <h2 id="modal-title" className="text-base font-semibold text-ink-900">
                    {title}
                  </h2>
                  {description ? (
                    <p id="modal-description" className="mt-0.5 text-sm text-ink-500">
                      {description}
                    </p>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Dialog schließen"
                  className="-m-1 rounded p-1 text-ink-500 hover:bg-ink-100 hover:text-ink-800"
                >
                  <Icon name="X" size={18} />
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>
              {footer ? (
                <div className="flex shrink-0 items-center justify-end gap-2 border-t border-ink-200 bg-ink-50 px-6 py-3">
                  {footer}
                </div>
              ) : null}
            </motion.div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );

  if (typeof document === 'undefined') return dialog;
  return createPortal(dialog, document.body);
}
